import { ArrowUpRight, CreditCard, ScrollText, FileCheck2, BadgeCheck, DollarSign, Ban, MapPin } from "lucide-react";
import FAQ from "@/components/FAQ";
import FinalCTA from "@/components/FinalCTA";
import { INCLUDED, FSA_EXAMPLES, EXISTING_PLAN } from "@/lib/ignitePlan";
import {
    IGNITE_CONSULT_URL,
    PAPERFORM_URL,
    SHORT_FORM_VIDEO_URL,
} from "@/lib/leads";

// New York variant: state + city + federal payroll tax savings stack to $813+/employee/year
const NY_TRUST = [
    {
        icon: DollarSign,
        t: "$813+/employee/year",
        b: "Average NY employer savings on FICA, FUTA, SUTA, and MCTMT combined — funded through the plan structure, not the P&L.",
    },
    {
        icon: ScrollText,
        t: "IRS Sections 105, 106, 213(d), 125",
        b: "Full plan documentation under the federal code sections that govern medical reimbursement and cafeteria plans.",
    },
    {
        icon: Ban,
        t: 'No "double dip"',
        b: "Reimbursements are backed by bona fide Section 213(d) services and actuarial fair market value — excluded from gross income under IRS 105(b).",
    },
    {
        icon: FileCheck2,
        t: "Free ACA reporting",
        b: "Form 1094 and 1095 reporting included at no cost — Part A requirements met affordably.",
    },
    {
        icon: BadgeCheck,
        t: "Audit ready",
        b: "Proper documentation, notices, and substantiation are in place from day one — full audit readiness.",
    },
];

const NY_FAQS = [
    {
        q: "Why is the number higher for New York?",
        a: "New York employers stack federal payroll tax savings (FICA, FUTA) with New York State SUTA and, for many, NYC MCTMT — averaging around $813 per eligible employee per year, versus roughly $620 nationally.",
    },
    {
        q: "Do we have to change our current health plan?",
        a: "No. Ignite Health works alongside your existing medical plan — same coverage, more support, greater value. There's no carrier switch and no renewal-date dependency.",
    },
    {
        q: "How long does implementation take?",
        a: "About two weeks from start to finish once you move forward — a straightforward setup with minimal disruption to your team.",
    },
    {
        q: "Do employees have to sign up?",
        a: "No. Once an employer implements the plan, eligible employees are automatically enrolled — no individual signup or action required on their part.",
    },
    {
        q: "What does this cost?",
        a: "Nothing for employees — the plan carries no out-of-pocket cost and doesn't reduce take-home pay. For employers, there's a small program fee typically more than offset by the $813+ in payroll tax savings per employee.",
    },
];

export default function NYLanding() {
    return (
        <div data-testid="page-ny">
            {/* Hero */}
            <section className="section pt-16 md:pt-24" data-testid="ny-hero">
                <div className="container-x">
                    <div className="max-w-4xl space-y-7">
                        <p className="eyebrow inline-flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5" strokeWidth={2} />
                            For New York employers
                        </p>
                        <h1 className="h-display">
                            Save{" "}
                            <span className="text-navy">$813 per employee, per year</span>
                            {" "}— without changing your existing health plan.
                        </h1>
                        <p className="lead max-w-2xl">
                            Ignite Health gives eligible NY employers access to
                            telemedicine, mental health support, prescription
                            coverage, preventive care, and an FSA card — at no
                            out-of-pocket cost to employees, and roughly $813 in
                            average annual payroll tax savings per employee for
                            businesses paying NYS SUTA (and MCTMT where
                            applicable).
                        </p>
                        <p className="text-sm text-ink-soft max-w-2xl">
                            Built around IRS Sections 105, 106, 213(d), and
                            125, with free ACA compliance reporting included.
                        </p>
                        <div className="flex flex-col gap-4 items-start pt-2">
                            <a
                                href={PAPERFORM_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="ny-hero-cta-primary"
                                className="inline-flex items-center gap-2.5 bg-brand text-brand-foreground hover:bg-brand-hover px-8 py-4 rounded-sm text-base font-semibold tracking-wide transition-colors shadow-sm hover:shadow"
                            >
                                Get Your Free NY Benefits Assessment
                                <ArrowUpRight className="h-5 w-5" strokeWidth={1.75} />
                            </a>
                            <a
                                href={IGNITE_CONSULT_URL}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="ny-hero-cta-supporting"
                                className="text-sm text-ink-soft hover:text-brand transition-colors"
                            >
                                Already know what you need?{" "}
                                <span className="underline underline-offset-4 decoration-border">
                                    Book a consult directly with Ignite Health
                                </span>{" "}
                                →
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Video */}
            <section className="pb-16 md:pb-20 pt-2" data-testid="ny-video">
                <div className="container-x max-w-4xl">
                    <div className="mb-6">
                        <p className="eyebrow mb-3">The 2-minute overview</p>
                        <h2 className="font-heading text-2xl md:text-3xl text-navy font-semibold">
                            See how Ignite Health works.
                        </h2>
                    </div>
                    <div className="aspect-video w-full border border-border bg-black overflow-hidden">
                        <video
                            className="w-full h-full"
                            controls
                            playsInline
                            preload="metadata"
                            data-testid="ny-video-player"
                        >
                            <source src={SHORT_FORM_VIDEO_URL} type="video/mp4" />
                            Your browser doesn't support embedded video.
                        </video>
                    </div>
                </div>
            </section>

            {/* Trust — NY variant with $813 headline stat */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="ny-trust"
            >
                <div className="container-x">
                    <div className="max-w-2xl mb-12">
                        <p className="eyebrow mb-4">Why NY employers</p>
                        <h2 className="h2">
                            The New York math is{" "}
                            <span className="text-navy">even better.</span>
                        </h2>
                        <p className="lead mt-5">
                            Federal + New York State + (where applicable) NYC
                            payroll taxes stack — pushing per-employee savings
                            past $813 per year for most NY businesses.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-border">
                        {NY_TRUST.map((t) => {
                            const Icon = t.icon;
                            return (
                                <div
                                    key={t.t}
                                    className="bg-background p-6 space-y-3"
                                    data-testid={`ny-trust-${t.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <Icon className="h-5 w-5 text-brand" strokeWidth={1.5} />
                                    <p className="font-heading text-base leading-snug">
                                        {t.t}
                                    </p>
                                    <p className="text-xs text-ink-soft leading-relaxed">
                                        {t.b}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* What's included */}
            <section className="section border-t border-border" data-testid="ny-included">
                <div className="container-x">
                    <div className="max-w-2xl mb-12">
                        <p className="eyebrow mb-4">What's included</p>
                        <h2 className="h2">
                            A full benefits stack —{" "}
                            <span className="text-navy">no out-of-pocket cost to employees.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                        {INCLUDED.map((b) => {
                            const Icon = b.icon;
                            return (
                                <div
                                    key={b.t}
                                    className="bg-background p-8 space-y-4"
                                    data-testid={`ny-included-${b.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <Icon className="h-6 w-6 text-brand" strokeWidth={1.5} />
                                    <h3 className="font-heading text-xl">{b.t}</h3>
                                    <p className="body text-sm">{b.b}</p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-6 border border-border bg-background p-8 md:p-10 grid lg:grid-cols-12 gap-8">
                        <div className="lg:col-span-5 space-y-4">
                            <CreditCard className="h-6 w-6 text-brand" strokeWidth={1.5} />
                            <h3 className="font-heading text-xl">
                                Supplemental Benefits — Dental + FSA Card
                            </h3>
                            <p className="body text-sm">
                                Extra savings go toward a dental plan and a
                                prepaid FSA card employees can use on eligible
                                healthcare purchases.
                            </p>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="eyebrow mb-4 text-ink-soft/80">
                                What employees can buy with the FSA card
                            </p>
                            <ul className="flex flex-wrap gap-2">
                                {FSA_EXAMPLES.map((item) => (
                                    <li
                                        key={item}
                                        className="inline-flex items-center border border-border px-3 py-1.5 text-xs text-ink-soft bg-surface"
                                    >
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Works alongside existing plan */}
            <section
                className="section bg-surface border-t border-border"
                data-testid="ny-existing-plan"
            >
                <div className="container-x">
                    <div className="max-w-2xl mb-12">
                        <p className="eyebrow mb-4">
                            Works alongside your existing plan
                        </p>
                        <h2 className="h2">
                            Same coverage.{" "}
                            <span className="text-navy">More support. Greater value.</span>
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-8">
                        {EXISTING_PLAN.map((e) => {
                            const Icon = e.icon;
                            return (
                                <div
                                    key={e.t}
                                    className="space-y-4"
                                    data-testid={`ny-existing-plan-${e.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                >
                                    <Icon className="h-6 w-6 text-brand" strokeWidth={1.5} />
                                    <h3 className="font-heading text-xl">{e.t}</h3>
                                    <p className="body text-sm">{e.b}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="section border-t border-border" data-testid="ny-faq">
                <div className="container-x">
                    <div className="max-w-2xl mb-10">
                        <p className="eyebrow mb-4">Common questions from NY employers</p>
                        <h2 className="h2">Straight answers.</h2>
                    </div>
                    <FAQ items={NY_FAQS} testIdPrefix="ny-faq" />
                </div>
            </section>

            {/* Final CTA */}
            <FinalCTA
                eyebrow="Ready when you are"
                heading="Start the HR Connect × Ignite Health path."
                body="Get your free New York benefits assessment, or book directly with Ignite Health if you're ready now."
                testIdPrefix="ny-final"
            />
        </div>
    );
}
